/**
 * An ended sign-in is a signed-out person, on the client too.
 *
 * The API session ends 30 days after sign-in, or sooner once unused for seven days or signed out elsewhere. Three places catch
 * that, so the screens never show "the connection failed": the proxy (auth.config.ts `authorized`: a page redirects to /login, an /api call answers 401), the shell
 * (AppShell: `useSession()` says "unauthenticated" and goes to /login), and lib/apiCall.ts, the one helper the screens call the
 * API through, which goes to /login on a 401 that means "not signed in".
 */

const NOT_ON_THESE_PAGES = ["/login", "/forgot-password", "/reset-password", "/upload"];
const under = (path: string, bases: string[]) => bases.some((b) => path === b || path.startsWith(b + "/"));

/**
 * A 401 that means the sign-in has ended: this app's route answer (`{ error: "Unauthorized" }`) or the API's problem title
 * ("Not signed in"). A 401 from a service secret or a refused credential says neither and is left to its screen.
 */
export function isNotSignedIn(status: number, body: unknown): boolean {
  if (status !== 401 || typeof body !== "object" || body === null) return false;
  const b = body as { error?: unknown; title?: unknown };
  return b.error === "Unauthorized" || b.title === "Not signed in";
}

/** `/login?callbackUrl=<this page>`, or null when this page is already one that needs no sign-in (never loop on /login). */
export function loginUrlFor(location: { pathname: string; search: string; hash?: string }): string | null {
  if (under(location.pathname, NOT_ON_THESE_PAGES)) return null;
  return `/login?callbackUrl=${encodeURIComponent(location.pathname + location.search + (location.hash ?? ""))}`;
}

let leaving = false;
/** Sends the browser to sign in, once. False when it is already on a page that needs no sign-in. */
export function goToLogin(location: {
  pathname: string;
  search: string;
  hash?: string;
  assign(url: string): void;
}): boolean {
  const to = loginUrlFor(location);
  if (!to) return false;
  if (!leaving) {
    leaving = true;
    location.assign(to);
  }
  return true;
}
export const resetLeavingForTests = (): void => {
  leaving = false;
};

/**
 * Is the person signed out, as the shell sees it? auth.ts answers `{}` (not null) when the API no longer knows the session, and
 * SessionProvider counts `{}` as "authenticated", so a session with no user is signed out too.
 */
export function isSignedOut(
  status: "loading" | "authenticated" | "unauthenticated",
  data: { user?: unknown } | null | undefined
): boolean {
  return status === "unauthenticated" || (status === "authenticated" && !data?.user);
}
